import React, { ComponentProps, useMemo } from 'react';

import PropTypes from 'prop-types';

import { StylableComponent, stylePropType } from '@adsk/alloy-react-helpers';

import { MENTION_TAG_REGEX } from '../consts';
import reactStringReplace from '../utils/reactStringReplace';
import MentionCallout from './MentionCallout';

export type TextProps = StylableComponent<HTMLSpanElement> & {
  value: string;
  regex?: RegExp;
  renderCallout?: (props: ComponentProps<typeof MentionCallout>) => JSX.Element;
};

const MentionText = React.forwardRef<HTMLSpanElement, TextProps>(
  (
    {
      className,
      style,
      value,
      regex = MENTION_TAG_REGEX,
      renderCallout = (rest: ComponentProps<typeof MentionCallout>) => (
        <MentionCallout {...rest} />
      ),
      ...props
    },
    ref
  ) => {
    const replacedStrings = useMemo(
      () =>
        reactStringReplace(value, regex, (mention, index) =>
          renderCallout({
            key: `${mention.id}_${index}`,
            mention,
          })
        ),
      [regex, renderCallout, value]
    );
    return (
      <span
        ref={ref}
        className={className}
        css={[style, { whiteSpace: 'pre-wrap' }]}
        {...props}
      >
        {replacedStrings}
      </span>
    );
  }
);

MentionText.displayName = 'MentionText';

MentionText.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Mention text value */
  value: PropTypes.string.isRequired,
  /** regex used to parse mentions from value */
  regex: PropTypes.instanceOf(RegExp),
  /** render Callout prop used to overwrite Callout  */
  renderCallout: PropTypes.func,
};

export default MentionText;
